import api from '@/store/api';
import { athena, unity } from '@/store/api/instances';
import type { ApiExportRequest, ApiListingRequest, ApiResponse } from '@bitstopco/bitstop-theme';

import type {
  AtmCoinsRequest,
  CreateWalletRequest,
  GenerateAddressesRequest,
  ProviderCredentialsRequest,
  UpdateWalletRequest,
  WalletCoinToggleRequest,
  WalletProvider,
  WalletsTransaction,
} from '@/types';

/**
 * Wallets RTK-Query API
 * For more information about RTK-Query see
 * * https://redux-toolkit.js.org/rtk-query/overview
 */
export const walletsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getWalletsConfig: build.query<ApiResponse<WalletProvider[]>, void>({
      query: () => athena({ url: 'v2/coins/operator-config' }),
    }),
    putProviderCredentials: build.mutation<void, ProviderCredentialsRequest>({
      query: (request) => {
        const { id, ...data } = request;
        return unity({ url: `providers/${id}/credentials`, method: 'PUT', data });
      },
    }),
    createWallet: build.mutation<void, CreateWalletRequest>({
      query: (request) => {
        const { provider_id, ...data } = request;
        return unity({ url: `providers/${provider_id}/wallets`, method: 'POST', data });
      },
    }),
    updateWallet: build.mutation<void, UpdateWalletRequest>({
      query: (request) => {
        const { wallet_id, ...data } = request;
        return unity({ url: `wallets/${wallet_id}`, method: 'PUT', data });
      },
    }),
    toggleCoin: build.mutation<void, WalletCoinToggleRequest>({
      query: (request) => {
        const { action, ...data } = request;
        return unity({ url: `coins/${action}`, method: 'POST', data });
      },
    }),
    putAtmCoins: build.mutation<void, AtmCoinsRequest>({
      query: (data) => unity({ url: 'coins/atms', method: 'PATCH', data }),
      invalidatesTags: ['Machines'],
    }),
    generateWalletsAddresses: build.mutation<
      ApiResponse<{ addresses: string[] }>,
      GenerateAddressesRequest
    >({
      query: (request) => {
        const { wallet_id, ...data } = request;
        return unity({ url: `wallets/${wallet_id}/generate-addresses`, method: 'POST', data });
      },
    }),
    getWalletsTransactions: build.query<ApiResponse<WalletsTransaction[]>, ApiListingRequest>({
      query: (data) => athena({ url: '/v2/coins/transactions', method: 'POST', data }),
    }),
    getWalletsExport: build.mutation<ApiResponse<BlobPart>, ApiExportRequest>({
      query: ({ type, data }) =>
        athena({ url: `/v2/coins/transactions/${type}`, method: 'POST', data }),
    }),
  }),
});

export const {
  useGetWalletsConfigQuery,
  usePutProviderCredentialsMutation,
  useCreateWalletMutation,
  useUpdateWalletMutation,
  useToggleCoinMutation,
  useGenerateWalletsAddressesMutation,
  useGetWalletsTransactionsQuery,
  useGetWalletsExportMutation,
  usePutAtmCoinsMutation,
} = walletsApi;
